import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { PropsWithChildren } from 'react';
import { Minus, Plug2, Plus, Trash2 } from 'lucide-react';

import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { useCreateD2KEnvironmentsMutation } from '@/react/portainer/environments/queries/useCreateEnvironmentMutation';
import { useEnvironmentList } from '@/react/portainer/environments/queries/useEnvironmentList';
import {
  Environment,
  EnvironmentType,
} from '@/react/portainer/environments/types';
import { getD2KUnsupportedReason } from '@/react/portainer/environments/utils';

import { Button } from '@@/buttons';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { FormControl } from '@@/form-components/FormControl';
import { FormError } from '@@/form-components/FormError';
import { Input, Select } from '@@/form-components/Input';
import { TextTip } from '@@/Tip/TextTip';
import { SwitchField } from '@@/form-components/SwitchField';

import { AnalyticsStateKey } from '../types';
import { MoreSettingsSection } from '../shared/MoreSettingsSection';

import { MAX_D2K_INSTANCES, remove, rename, resize } from './instances';
import { D2KInstance, FormValues } from './types';
import { validation } from './validation';

interface Props {
  onCreate(environment: Environment, analytics: AnalyticsStateKey): void;
}

const kubernetesTypes = [
  EnvironmentType.KubernetesLocal,
  EnvironmentType.AgentOnKubernetes,
  EnvironmentType.EdgeAgentOnKubernetes,
];

const initialValues: FormValues = {
  parentEnvironmentId: 0,
  instances: [{ name: '', auto: false }],
  swarmMode: false,
  meta: { groupId: 1, tagIds: [] },
};

export function WizardD2K({ onCreate }: Props) {
  const mutation = useCreateD2KEnvironmentsMutation();

  return (
    <div className="form-horizontal">
      <TextTip color="blue">
        Portainer installs d2k in a namespace, d2k-&lt;name&gt;, of a Kubernetes
        environment with an Edge agent, for each instance, and adds a D2K
        environment that serves its Docker API through the Edge agent. Deleting
        a D2K environment leaves its d2k and workloads in the cluster.
      </TextTip>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validation}
        validateOnMount
      >
        {({ isValid, dirty }) => (
          <Form className="mt-4">
            <ParentEnvironmentField />

            <AfterParentChosen>
              <InstancesField />

              <SwarmModeField />

              <MoreSettingsSection />

              <div className="form-group">
                <div className="col-sm-12">
                  <LoadingButton
                    className="wizard-connect-button vertical-center"
                    data-cy="d2k-create-button"
                    loadingText="Installing D2K..."
                    isLoading={mutation.isLoading}
                    disabled={!dirty || !isValid}
                    icon={Plug2}
                  >
                    Install and connect
                  </LoadingButton>
                </div>
              </div>
            </AfterParentChosen>
          </Form>
        )}
      </Formik>
    </div>
  );

  function handleSubmit(
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) {
    mutation.mutate(
      {
        parentEnvironmentId: values.parentEnvironmentId,
        instances: values.instances.map(({ name }) => ({ name })),
        swarmMode: values.swarmMode,
        meta: values.meta,
      },
      {
        onSuccess(results) {
          const failed: D2KInstance[] = [];
          results.forEach((result, index) => {
            if (result.environment) {
              notifySuccess('D2K environment created', result.environment.Name);
              onCreate(result.environment, 'd2k');
              return;
            }

            notifyError(
              `Unable to create the D2K instance ${result.name}`,
              undefined,
              result.error
            );
            failed.push({ name: values.instances[index].name, auto: false });
          });

          // Keep the instances that failed, to try them again
          resetForm({
            values: {
              ...values,
              instances: failed.length ? failed : initialValues.instances,
            },
          });
        },
      }
    );
  }
}

function ParentEnvironmentField() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { environments, isLoading } = useEnvironmentList({
    types: kubernetesTypes,
    excludeSnapshots: true,
    pageLimit: 0,
  });

  const options = [
    {
      value: 0,
      label: isLoading
        ? 'Loading Kubernetes environments...'
        : 'Select a Kubernetes environment',
      disabled: true,
    },
    ...environments.map((environment) => {
      const reason = getD2KUnsupportedReason(environment);
      return {
        value: environment.Id,
        label: reason ? `${environment.Name} (${reason})` : environment.Name,
        disabled: !!reason,
      };
    }),
  ];

  return (
    <FormControl
      size="medium"
      label="Kubernetes environment"
      inputId="d2k-parent-environment"
      tooltip="The Kubernetes environment D2K runs in. It needs an Edge agent, 3.0.0 or later, connected through a tunnel: not in async mode."
      required
    >
      <Select
        id="d2k-parent-environment"
        data-cy="d2k-parent-environment-select"
        value={values.parentEnvironmentId}
        options={options}
        onChange={(e) =>
          setFieldValue('parentEnvironmentId', parseInt(e.target.value, 10))
        }
      />
    </FormControl>
  );
}

function InstancesField() {
  const { values, setFieldValue, errors } = useFormikContext<FormValues>();
  const { instances } = values;
  const instanceErrors = Array.isArray(errors.instances)
    ? errors.instances
    : [];

  return (
    <>
      <FormControl
        size="medium"
        label="Quantity"
        inputId="d2k-quantity"
        tooltip={`Install up to ${MAX_D2K_INSTANCES} D2K instances at once, each with the same settings.`}
      >
        <div className="flex w-fit items-stretch">
          <Button
            color="default"
            icon={Minus}
            className="!ml-0 !rounded-r-none"
            onClick={() =>
              setInstances(resize(instances, instances.length - 1))
            }
            disabled={instances.length <= 1}
            data-cy="d2k-quantity-decrease"
            aria-label="Fewer instances"
          />
          <Input
            id="d2k-quantity"
            type="number"
            min={1}
            max={MAX_D2K_INSTANCES}
            className="!w-20 !rounded-none text-center"
            value={instances.length}
            onChange={(e) => {
              const quantity = parseInt(e.target.value, 10);
              if (!Number.isNaN(quantity)) {
                setInstances(resize(instances, quantity));
              }
            }}
            data-cy="d2k-quantity-input"
            aria-label="D2K instances quantity"
          />
          <Button
            color="default"
            icon={Plus}
            className="!ml-0 !rounded-l-none"
            onClick={() =>
              setInstances(resize(instances, instances.length + 1))
            }
            disabled={instances.length >= MAX_D2K_INSTANCES}
            data-cy="d2k-quantity-increase"
            aria-label="More instances"
          />
        </div>
      </FormControl>

      <FormControl
        size="medium"
        label="Name"
        inputId="d2k-instance-name_0"
        tooltip="Each instance runs in its own namespace, d2k-<name>, and gets a D2K environment of the same name."
        errors={
          typeof errors.instances === 'string' ? errors.instances : undefined
        }
        required
      >
        <div className="flex flex-col gap-2">
          {instances.map((instance, index) => {
            const error = instanceErrors[index];
            return (
              <div key={index}>
                <div className="flex items-center gap-2">
                  <Input
                    id={`d2k-instance-name_${index}`}
                    value={instance.name}
                    onChange={(e) =>
                      setInstances(rename(instances, index, e.target.value))
                    }
                    placeholder={index === 0 ? 'e.g. team-a' : ''}
                    aria-label="D2K instance name"
                    data-cy={`d2k-instance-name_${index}`}
                  />
                  {index > 0 ? (
                    <Button
                      color="dangerlight"
                      icon={Trash2}
                      onClick={() => setInstances(remove(instances, index))}
                      data-cy={`d2k-instance-remove_${index}`}
                      aria-label={`Remove ${instance.name || 'instance'}`}
                    />
                  ) : (
                    // keeps the first input as wide as the others
                    <span className="w-[34px] shrink-0" />
                  )}
                </div>
                {error && typeof error !== 'string' && error.name && (
                  <FormError>{error.name}</FormError>
                )}
                {instance.name && !error && (
                  <div className="small text-muted mt-1">
                    Namespace d2k-{instance.name}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </FormControl>
    </>
  );

  function setInstances(next: D2KInstance[]) {
    setFieldValue('instances', next);
  }
}

/**
 * AfterParentChosen shows its children once a Kubernetes environment is
 * chosen: the rest of the form depends on it.
 */
function AfterParentChosen({ children }: PropsWithChildren<unknown>) {
  const { values } = useFormikContext<FormValues>();

  if (!values.parentEnvironmentId) {
    return (
      <TextTip color="blue">
        Select the Kubernetes environment to run D2K in to continue.
      </TextTip>
    );
  }

  return <>{children}</>;
}

function SwarmModeField() {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  return (
    <div className="form-group">
      <div className="col-sm-12">
        <SwitchField
          label="Swarm mode"
          labelClass="col-sm-4 col-lg-3"
          tooltip="d2k emulates a Docker Swarm manager rather than a standalone Docker engine: its D2K environments are Swarm environments, with services, configs and secrets, and Swarm stacks. It can't be changed later."
          checked={values.swarmMode}
          onChange={(swarmMode) => setFieldValue('swarmMode', swarmMode)}
          data-cy="d2k-swarm-mode-switch"
        />
      </div>
    </div>
  );
}
